/**
 * `useUnreadCount(context?)` — unread-badge hook for the messaging platform
 * (M-MSG-2, architecture-messaging-platform.md §9.1 / §3.3).
 *
 *   - With a `context` → per-thread badge ({@link getThreadUnreadCount}).
 *   - Without a `context` → global nav badge ({@link getGlobalUnreadCount},
 *     §3.3 v1 single-aggregate variant).
 *
 * Scope/reader come from `useAuth()` (`effectiveScopeUid` / `currentUser.uid`).
 *
 * v1 note: this is a one-shot `count()` fetch that re-runs when the auth scope or
 * the context identity changes — not a live subscription. Badge refresh after
 * `markThreadRead`/own sends is driven by the consuming UI (M-MSG-3/4) re-mounting
 * or changing deps. Errors fail SAFE to 0 (never block the UI), and are logged.
 *
 * @module useUnreadCount
 */

import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { MessageContext } from '../types/messagingTypes';
import { getThreadUnreadCount, getGlobalUnreadCount } from '../api/messagesApi';
import logger from '../utils/logger';

/**
 * Returns the unread-message count for a thread (with `context`) or globally
 * (without). Fails safe to 0.
 *
 * @param context - Optional thread context; omit for the global nav badge.
 * @returns The (approximate for global, §3.3) unread count.
 */
export function useUnreadCount(context?: MessageContext): number {
  const { currentUser, effectiveScopeUid } = useAuth();
  const [count, setCount] = useState<number>(0);

  const ctxType = context?.type ?? null;
  const ctxRefId = context?.refId ?? null;

  useEffect(() => {
    if (!currentUser) {
      setCount(0);
      return;
    }

    let cancelled = false;
    const readerUid = currentUser.uid;
    const scopeUid = effectiveScopeUid;

    const run = async (): Promise<void> => {
      try {
        const next = ctxType
          ? await getThreadUnreadCount({ type: ctxType, refId: ctxRefId }, scopeUid, readerUid)
          : await getGlobalUnreadCount(scopeUid, readerUid);
        if (!cancelled) {
          setCount(next);
        }
      } catch (err) {
        logger.warn(
          'useUnreadCount: count failed — failing safe to 0',
          err instanceof Error ? err : new Error(String(err)),
        );
        if (!cancelled) {
          setCount(0);
        }
      }
    };

    void run();

    return () => {
      cancelled = true;
    };
  }, [currentUser, effectiveScopeUid, ctxType, ctxRefId]);

  return count;
}
